/** Purposeful work buildings share facade construction and reachable use points.
 * All coordinates are local; the place assembler mirrors the complete plan. */
import type {AssetVec3} from '../proceduralAssets.js';
import type {FrontierBuilder} from './frontierExtras.js';
import type {FrontierPlacePlan} from './frontierTown.js';
import {buildFacade,type FacadePlan} from './facade.js';
import {counterStand,counterTop} from './counterContact.js';
import * as pmath from '../pmath.js';
export interface WorkplacePlan {
  entrance:AssetVec3; facades:FacadePlan[];
  /** The reserved slot that owns this routine; visitors keep their own position. */
  workerSlot?:string;
  plots?:GrowingBedPlan[];
  destinations:{id:string;position:AssetVec3}[];
  route:AssetVec3[]; firstFacing:AssetVec3; lastFacing:AssetVec3;
  firstEmote:string; lastEmote:string;
}
export interface GrowingBedPlan {
  id:string; crop:'greens'|'roots'|'beans'; stage:'seedling'|'growing'|'mature';
  center:AssetVec3; size:AssetVec3; workPosition:AssetVec3;
}
/** Open forge hall + daylight repair bench + quenching and material storage. */
export function workshopDetails(b:FrontierBuilder,d:FrontierPlacePlan['design']):WorkplacePlan {
  const {part,box,node}=b;
  const facades:FacadePlan[]=[
    {id:'workshop:rear',center:[0,.3,-3],axis:'x',outward:-1,width:8,height:3.7,thickness:.3,openings:[{id:'bench-light',u:2,bottom:1.65,width:d.workshopForm==='cross-bay'?2.4:2,height:1.5,family:'transom'}]},
    {id:'workshop:forge-wall',center:[-4,.3,0],axis:'z',outward:-1,width:6,height:3.7,thickness:.3,openings:[{id:'vent',u:-1.4,bottom:2.4,width:1.3,height:.8,family:'transom'}]},
  ];
  facades.forEach(f=>buildFacade(b,f));
  part('workshop:work-surfaces',()=>{
    // Stone beneath the hot work; timber beneath the repair bench. Thin
    // finishes sit on the shared slab and do not introduce a walking step.
    box([3.5,.014,3.3],[-2,.303,-1.2],4);
    const boards=node('box',[.34,.012,4.8],[.4,.302,-.3],12);
    boards.modifiers=[{type:'array',mode:'linear',count:10,offset:[.35,0,0]}];
  });
  part('workshop:tool-wall',()=>{
    box([1.45,.8,.12],[-.1,2.15,-2.77],12);
    for(const x of [-.3,.15,.6]){
      box([.07,.53,.07],[x,2.05,-2.64],1);
      box([.28,.12,.13],[x,2.3,-2.64],7);
    }
  });
  part('workshop:repair-bench',()=>{
    box([.7,.13,.4],[2,1.505,-1],12);
    box([.24,.27,.28],[1.2,1.57,-.83],7);
    box([.08,.08,.55],[1.2,1.66,-.62],7);
    const boards=node('box',[.18,.055,.65],[1.6,1.49,-1.05],12);
    boards.modifiers=[{type:'array',mode:'linear',count:4,offset:[.21,0,0]}];
  });
  part('workshop:quench',()=>{
    const x=-3.1,z=1.8;
    box([1.2,.16,.85],[x,.4,z],4,true);
    for(const xx of [x-.53,x+.53])box([.14,.55,.85],[xx,.7,z],4,true);
    for(const zz of [z-.35,z+.35])box([1.05,.55,.15],[x,.7,zz],4,true);
    box([.91,.025,.56],[x,.76,z],13);
  });
  part('workshop:materials',()=>{
    for(const z of [-2.25,-.2])box([.38,.16,.18],[3.45,.4,z],1,true);
    const stock=node('box',[.13,.14,2.3],[3.28,.54,-1.2],7);
    stock.modifiers=[{type:'array',mode:'linear',count:3,offset:[.15,0,0]}];
    box([.65,.32,2.3],[3.43,.46,-1.2],1,true);
  });
  part('workshop:trade-sign',()=>{
    box([.12,.12,.7],[3.7,3.2,3.35],1);
    box([.1,.65,.6],[3.7,2.8,3.58],5);
    box([.13,.12,.4],[3.64,2.9,3.58],4);
  });
  const route:AssetVec3[]=[[2,.3,.15],[1.3,.3,1.35],[-1.9,.3,1.35],[-2.5,.3,-.8]];
  return {entrance:[2,0,5.375],facades,destinations:[{id:'repair',position:[2,.3,.15]},{id:'forge',position:[-2.5,.3,-.8]},{id:'quench',position:[-2.1,.3,1.8]}],route,firstFacing:[0,0,-1],lastFacing:[0,0,-1],firstEmote:'emo_fixing',lastEmote:'emo_fixing'};
}
/** Storehouse circulation: receiving threshold → inventory desk → rear stock.
 * Racks occupy wall bands and leave the middle as a continuous working aisle. */
export function frontierStorehouse(b:FrontierBuilder,d:FrontierPlacePlan['design']):WorkplacePlan {
  const {part,box,node,floor,roof,post,crate,barrel,slot}=b;
  const w=d.homeWidth-.4,half=w/2,depth=6.4,front=3.2,h=3.7,doorX=-1.5,doorW=2.5;
  floor(w+.6,depth+.6);
  part('storehouse:timber-floor',()=>{
    const count=Math.ceil((w-.35)/.45),pitch=(w-.35)/count;
    const board=node('box',[pitch-.014,.012,depth-.35],[-(w-.35)/2+pitch/2,.302,0],12);
    board.modifiers=[{type:'array',mode:'linear',count,offset:[pitch,0,0]}];
  });
  const facades:FacadePlan[]=[
    {id:'storehouse:receiving',center:[0,.3,front],axis:'x',outward:1,width:w,height:h,thickness:.3,openings:[{id:'loading-door',u:doorX,bottom:.3,width:doorW,height:2.8,family:'door'},{id:'office',u:half-1.1,bottom:1.65,width:1.1,height:1.3,family:'casement'}]},
    {id:'storehouse:rear',center:[0,.3,-front],axis:'x',outward:-1,width:w,height:h,thickness:.3,openings:[{id:'clerestory',u:0,bottom:2.4,width:2,height:.85,family:'transom'}]},
    {id:'storehouse:side',center:[-half,.3,0],axis:'z',outward:-1,width:depth,height:h,thickness:.3,openings:[{id:'daylight',u:0,bottom:2.2,width:1.8,height:1.1,family:'transom'}]},
  ];
  facades.forEach(f=>buildFacade(b,f));box([.3,h,depth],[half,2.15,0],0,true);
  const slope=pmath.sin(d.roofPitch)/pmath.cos(d.roofPitch),eave=4.1,base=eave+.4*slope-.09/pmath.cos(d.roofPitch),rise=depth/2*slope;
  part('storehouse:roof',()=>{
    for(const x of [-half,half]){
      box([.3,base-4,depth],[x,(base+4)/2,0],0);
      const g=node('box',[.3,rise,depth],[x,base+rise/2,0],0);g.modifiers=[{type:'taper',axis:'y',factor:0}];
      box([.34,.18,depth],[x,base,0],1);
    }
    roof(w+.8,depth+.8,eave);
  });
  if(d.storehouseForm==='loft'){
    const ridge=eave+3.6*slope,bottom=ridge-.45;
    part('storehouse:roof-vent',()=>{
      box([3.2,1.5,2.4],[0,bottom+.75,-.4],0);
      for(const x of [-1.15,0,1.15])box([.6,.65,.08],[x,bottom+.8,.85],7);
      roof(3.8,3,bottom+1.5,0,-.4,.4);
    });
  }else{
    const x=half-1.6;
    part('storehouse:loading-porch',()=>{
      floor(3.4,3,x,front+1.35);
      for(const xx of [x-1.4,x+1.4])post(xx,front+2.65,2.8);
      for(const z of [front,front+2.65])box([3.4,.18,.2],[x,3.08,z],1);
      roof(3.4,2.9,3.1,x,front+1.2,.4);
    });
  }
  part('storehouse:open-loading-doors',()=>{
    // Fixed open leaves hinge from the jambs, with the full receiving path
    // between them. They are geometry/collision, not animated door entities.
    for(const side of [-1,1]){
      const x=doorX+side*(doorW/2+.08);
      box([.12,2.65,1.2],[x,1.665,front+.65],12,true);
      for(const y of [.75,2.55])box([.16,.09,1.18],[x,y,front+.65],1);
      for(const y of [.8,2.4])box([.18,.1,.28],[x,y,front+.12],7);
    }
  });
  floor(3,1.1,doorX,front+.6);floor(3.2,.8,doorX,front+1.3,.15);
  part('storehouse:racks',()=>{
    for(const side of [-1,1]){
      const x=side*(half-.65);
      for(const z of [-2.45,.45])box([.16,2.6,.16],[x,1.6,z],1,true);
      for(const y of [.65,1.45,2.25])box([.85,.13,3.15],[x,y,-1],12,true);
      for(const z of [-2,-.7]){
        box([.55,.58,.6],[x,.99,z],1,true);
        box([.58,.06,.64],[x,1.22,z],3);
      }
      for(const z of [-2,-1,0])node('cylinder',[.19,.23,.4],[x,1.72,z],4);
    }
  });
  part('storehouse:rear-stock',()=>{
    for(const x of [-1,1])crate(x,-2.4);
    barrel(0,-2.45);
  });
  const deskCenter:AssetVec3=[half-1.35,1.23,1.7],deskSize:AssetVec3=[1.45,.14,.72];
  part('storehouse:inventory-desk',()=>{
    const x=half-1.35;
    counterTop(b,deskCenter,deskSize,12,1);
    for(const xx of [x-.55,x+.55])post(xx,1.7,.86);
    box([.45,.06,.4],[x,1.34,1.7],6);
    node('cylinder',[.1,.11,.2],[x+.48,1.4,1.65],7);
  });
  part('storehouse:receiving-floor',()=>{
    const boards=node('box',[w-.4,.025,.26],[0,.307,2.35],12);
    boards.modifiers=[{type:'array',mode:'linear',count:3,offset:[0,0,.29]}];
    for(const x of [-half+.15,half-.15])box([.22,3.7,.25],[x,2.15,3.3],1);
  });
  const desk=counterStand(deskCenter,deskSize,1),stock:AssetVec3=[0,.3,-1.35];
  slot('store','inspect',desk,[0,.3,2.65],Math.PI);
  return {entrance:[doorX,0,4.9],facades,destinations:[{id:'receiving',position:[doorX,.3,2.65]},{id:'inventory',position:[...desk]},{id:'stock',position:[...stock]}],route:[[...desk],[0,.3,2.65],[...stock]],firstFacing:[0,0,-1],lastFacing:[0,0,-1],firstEmote:'emo_counter_idle',lastEmote:'emo_lookaround'};
}
